import axios from 'axios';
import { setupAxiosInterceptor } from './auth/axiosConfig';
import config from './config';

// Create an API instance
const adminApi = axios.create({
  baseURL: config.API_BASE_URL + '/cellcog/admin',
});

setupAxiosInterceptor(adminApi);

/** Hourly-cached admin metrics (CEL-1662/1673; cache-only, 503 when cold or stale). */
export const getLiveMetrics = () => adminApi.get('/live-metrics');
export const getScoreboardFull = () => adminApi.get('/scoreboard/full');
export const getHirersFull = () => adminApi.get('/hirers/full');
export const getPaymentsFull = () => adminApi.get('/payments/full'); // Admin > Payments (Oct 19 2026), same cache contract
export const getMarketingPagesFull = () => adminApi.get('/marketing-pages/full'); // Admin > Marketing Pages (Oct 19 2026), same cache contract

/**
 * Get list of all users (admin only)
 * @param {Object} params - Query parameters
 * @returns {Promise} Response with user list
 */
export const getAdminUsers = (params) => {
  return adminApi.get('/users', { params });
};

/**
 * Get list of all chats (admin only)
 * @param {Object} params - Query parameters
 * @returns {Promise} Response with chat list
 */
export const getAdminChats = (params) => {
  return adminApi.get('/chats', { params });
};

// Admin V2 lists (Oct 16 2026): agent_docs/admin_v2/ADMIN_V2_API_CONTRACT.md. Every call is a read except the unflag.

/** One user: the row plus wallets, the 50 newest ledger rows and every membership (contract §1.2). */
export const getAdminUser = (userId) => adminApi.get(`/users/${encodeURIComponent(userId)}`);

/** The "Signups to review" band: duplicate signups grouped by signup IP. params: { days, include_internal } (§1.3). */
export const getAdminUsersReview = (params) => adminApi.get('/users/review', { params });

/** One chat: the row plus 30 days of credits by day; also answers for AI employee chats (§2.2). */
export const getAdminChat = (chatId) => adminApi.get(`/chats/${encodeURIComponent(chatId)}`);

/** The "Locks to review" band: every chat locked right now, employee chats included. params: { include_internal } (§2.3). */
export const getAdminChatsReview = (params) => adminApi.get('/chats/review', { params });

/** AI employees list (§3.1). */
export const getAdminEmployees = (params) => adminApi.get('/employees', { params });

/** One AI employee by its anchor id (or document id): row, handles, recent shifts, 30-day usage (§3.2). */
export const getAdminEmployee = (employeeId) => adminApi.get(`/employees/${encodeURIComponent(employeeId)}`);

/** Clear a chat's lock, both classes at once; the reason is required and lands on the audit trail (§2.3). */
export const unflagAdminChat = (chatId, reason) => adminApi.post('/chats/unflag', { chat_id: chatId, reason });

/**
 * Outreach billing ledger (admin only) — the weekly-reconciliation observability surface:
 * charge schedule (which domain/mailbox/user wallet) + landed charge history + stats.
 * @param {Object} params - { history_days }
 * @returns {Promise} Response with { stats, schedule, history }
 */
export const getOutreachLedger = (params) => {
  return adminApi.get('/outreach/ledger', { params });
};

/**
 * Outreach leads by organization × source (admin only): verified / contacted / replied per cell,
 * registry cache hits and the computed verification savings.
 * @returns {Promise} Response with { rows, spend_avoided, generated_at }
 */
export const getOutreachLeadsBySource = () => {
  return adminApi.get('/outreach/leads-by-source');
};

/**
 * Start spoofing session (admin only)
 * @param {string} userId - User ID to spoof
 * @returns {Promise} Response with session details
 */
export const startSpoofingSession = (userId) => {
  return adminApi.post('/spoof/start', { user_id: userId });
};

/**
 * End spoofing session (admin only)
 * @returns {Promise} Response confirming session end
 */
export const endSpoofingSession = () => {
  return adminApi.post('/spoof/end');
};

/**
 * Get current spoofing status (admin only)
 * @returns {Promise} Response with spoofing status
 */
export const getSpoofingStatus = () => {
  return adminApi.get('/spoof/status');
};
